import { type Inject, type Scenario, eventPayloadSchema } from '@orgsim/shared';
import { useEffect, useState } from 'react';

type Props = {
  inject: Inject;
  scenario: Scenario;
  open: boolean;
  onOpen: () => void;
  onChange: (i: Inject) => void;
  onRemove: () => void;
  // The committed inject only ever holds the last effects value that parsed and validated, so
  // the editor cannot tell from `scenario` alone whether the instructor is mid-edit on invalid
  // JSON. This reports live validity up so EditorScreen can refuse to save while it's broken.
  onValidChange: (valid: boolean) => void;
};

export function InjectForm({
  inject,
  scenario,
  open,
  onOpen,
  onChange,
  onRemove,
  onValidChange,
}: Props) {
  const [effectsText, setEffectsText] = useState(() =>
    JSON.stringify(inject.effects ?? [], null, 2),
  );
  const [effectsError, setEffectsError] = useState<string | null>(null);
  const set = (patch: Partial<Inject>) => onChange({ ...inject, ...patch });

  useEffect(() => {
    onValidChange(effectsError === null);
  }, [effectsError, onValidChange]);

  const onEffects = (text: string) => {
    setEffectsText(text);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setEffectsError('Kein gültiges JSON');
      return;
    }
    if (!Array.isArray(parsed)) {
      setEffectsError('Wirkungen müssen eine JSON-Liste sein, z. B. [] oder [{...}].');
      return;
    }
    const bad = parsed.map((e) => eventPayloadSchema.safeParse(e)).find((r) => !r.success);
    if (bad && !bad.success) {
      setEffectsError(bad.error.issues[0]?.message ?? 'Ungültige Wirkung');
      return;
    }
    setEffectsError(null);
    set({ effects: parsed.length ? parsed : undefined });
  };

  const unitIds = scenario.units.map((u) => `${u.id} (${u.callsign})`).join(', ');

  return (
    <div className="rounded border border-neutral-800">
      <button type="button" onClick={onOpen} className="w-full px-2 py-1 text-left">
        {inject.title}
      </button>
      {open ? (
        <div className="space-y-2 border-t border-neutral-800 p-2">
          <label className="block">
            Titel
            <input
              value={inject.title}
              onChange={(e) => set({ title: e.target.value })}
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            />
          </label>
          <label className="block">
            Text (liest der Ausbilder vor)
            <textarea
              value={inject.text}
              onChange={(e) => set({ text: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1"
            />
          </label>
          <label className="block">
            Wirkungen (JSON, optional)
            <textarea
              value={effectsText}
              onChange={(e) => onEffects(e.target.value)}
              rows={6}
              className="mt-1 w-full rounded bg-neutral-800 px-2 py-1 font-mono text-xs"
            />
          </label>
          <p className="text-xs text-neutral-500">
            Beispiel: {'[{"type":"UnitStatusChanged","unitId":"...","status":"onScene"}]'}. Kräfte:{' '}
            {unitIds || 'keine'}.
          </p>
          {effectsError ? <p className="text-xs text-red-300">{effectsError}</p> : null}
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-neutral-400 hover:text-red-300"
          >
            Einspieler entfernen
          </button>
        </div>
      ) : null}
    </div>
  );
}
